import { useEffect, useRef, useState } from 'react'
import { Box, Grid, IconButton, Switch, Typography } from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import { useTranslation } from 'react-i18next'
import SupportListWrapper from 'core/page/SupportListWrapper'
import { fetchLogsAction } from './state/actions'
import { useLogsQueryByType } from './hooks/use-filter-logs'
import { RefreshDataFunc } from '@cbgms/components/grid/Knock/KnockDataLoader'
import { makeStyles } from '@mui/styles'

export interface LogQueryObject {
  error: string
  warn: string
  info: string
}

interface DetailsListProps {
  custEosId: string
}

const useStyles = makeStyles({
  typography: {
    fontSize: '13px',
    borderLeft: '0.1px solid #D4D4D4',
    borderRight: '0.1px solid #D4D4D4',
    borderTop: '0.1px solid #D4D4D4',
    padding: '10px',
    display: 'inline-block',
    backgroundColor: '#FFFFFF',
    boxShadow: 'none',
    fontWeight: '550',
    borderTopLeftRadius: '5px',
    borderTopRightRadius: '5px'
  },
  box: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '5px 0',
    border: '0.1px solid #D4D4D4',
    backgroundColor: '#FFFFFF',
    flexWrap: 'wrap',
    margin: '0 auto',
    outline: 'none'
  },
  iconButton: {
    color: '#0077CC'
  },
  switchBox: {
    display: 'flex',
    alignItems: 'center',
    width: '30%'
  },
  errorText: {
    color: '#D32F2F',
    fontWeight: '550',
    fontSize: '14px',
    marginLeft: '8px',
    marginRight: '4px'
  },
  warningText: {
    color: '#FF9800',
    fontWeight: '550',
    fontSize: '14px',
    marginRight: '4px'
  },
  infoText: {
    color: '#3F51B5',
    fontWeight: '550',
    fontSize: '14px',
    marginRight: '4px'
  }
})

const SwitchBox: React.FC<{
  label: string
  color: 'error' | 'warning' | 'info'
  checked: boolean
  onChange: (checked: boolean) => void
  className: string
}> = ({ label, color, checked, onChange, className }) => (
  <Box className={className}>
    <Typography className={className}>{label}</Typography>
    <Switch color={color} checked={checked} onChange={e => onChange(e.target.checked)} />
  </Box>
)

const DetailsList: React.FC<DetailsListProps> = ({ custEosId }) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const [showErrors, setShowErrors] = useState(true)
  const [showWarnings, setShowWarnings] = useState(true)
  const [showInfo, setShowInfo] = useState(true)
  const { setQueryType, apiSearchQuery } = useLogsQueryByType()
  const refreshData = useRef<RefreshDataFunc | undefined>(undefined)

  const handleRefresh = () => {
    setShowWarnings(true)
    setShowErrors(true)
    setShowInfo(true)
    const queryTypeFilter = {
      error: showErrors,
      warn: showWarnings,
      info: showInfo,
      customerEosId: custEosId,
      appId: 1
    }
    setQueryType(queryTypeFilter)
  }

  const renderToggleMenu = () => (
    <Grid item xs={12}>
      <div>
        <Typography variant='h6' className={classes.typography}>
          {t('AppLabels.log')}
        </Typography>
      </div>
      <Box className={classes.box}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '10%' }}>
          <IconButton onClick={handleRefresh} aria-label='refresh'>
            <RefreshIcon className={classes.iconButton} />
          </IconButton>
        </Box>

        <SwitchBox label={t('AppLabels.error')} color='error' checked={showErrors} onChange={setShowErrors} className={classes.switchBox} />
        <SwitchBox
          label={t('AppLabels.warning')}
          color='warning'
          checked={showWarnings}
          onChange={setShowWarnings}
          className={classes.switchBox}
        />
        <SwitchBox label={t('AppLabels.info')} color='info' checked={showInfo} onChange={setShowInfo} className={classes.switchBox} />
      </Box>
    </Grid>
  )

  useEffect(() => {
    const queryTypeFilter = {
      error: showErrors,
      warn: showWarnings,
      info: showInfo,
      customerEosId: custEosId,
      appId: 1
    }
    setQueryType(queryTypeFilter)
  }, [showErrors, showWarnings, showInfo])

  return (
    <SupportListWrapper
      scope='customers'
      loadAction={fetchLogsAction}
      type='list-paginated-log'
      translationKey='CustomerLog'
      fullWidthTabItems={renderToggleMenu()}
      predefinedSearch={apiSearchQuery}
      hideExport
      hideActions
      hideDefaultMenuItems
      noDataPlaceholder={t('Customers.NoCustomerFound')}
      onDataLoaderInitialized={newRefreshdata => (refreshData.current = newRefreshdata)}
    />
  )
}

export default DetailsList
